import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Braces,
  Check,
  ChevronRight,
  CircleCheck,
  FileCheck2,
  FileText,
  Fingerprint,
  Globe2,
  IdCard,
  Layers3,
  LockKeyhole,
  Menu,
  ScanFace,
  ShieldCheck,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import {
  DOCUMENT_TYPES,
  MULTI_DOCUMENT_COUNTRY_COUNT,
  documentRequiresBack,
  getAllowedDocumentTypes,
  supportsMultipleDocumentTypes,
} from "../../features/kyc/services/documentRequirements";
import "./landing.scss";

const NAV_ITEMS = [
  { label: "Live demo", href: "#demo" },
  { label: "Platform", href: "#platform" },
  { label: "Journey", href: "#flow" },
  { label: "Extensibility", href: "#extensibility" },
];

const DEMO_COUNTRIES = [
  { code: "SE", label: "Sweden" },
  { code: "US", label: "United States" },
  { code: "JP", label: "Japan" },
  { code: "RO", label: "Romania" },
  { code: "BR", label: "Brazil" },
  { code: "IN", label: "India" },
];

const DOCUMENT_META = {
  [DOCUMENT_TYPES.PASSPORT]: {
    label: "Passport",
    detail: "Photo page",
    icon: FileText,
  },
  [DOCUMENT_TYPES.ID_CARD]: {
    label: "Identity card",
    detail: "Front and back",
    icon: IdCard,
  },
  [DOCUMENT_TYPES.DRIVER_LICENSE]: {
    label: "Driver's license",
    detail: "Front and back",
    icon: IdCard,
  },
};

const JOURNEY_STEPS = [
  {
    number: "01",
    title: "Read the country",
    description:
      "The session country determines whether the applicant sees three document choices or a passport-only route.",
  },
  {
    number: "02",
    title: "Collect the right sides",
    description:
      "Passport needs one photo page. Identity cards and driver's licenses require front and back.",
  },
  {
    number: "03",
    title: "Guide each capture",
    description:
      "Document framing, face movement, and address evidence stay inside one focused mobile journey.",
  },
  {
    number: "04",
    title: "Keep deeper checks pluggable",
    description:
      "Provider gateways are ready for document authenticity and biometric liveness APIs when configured.",
  },
];

const CODE_SAMPLE = [
  "{",
  '  "session_id": "vs_7k2m...",',
  '  "country": "SE",',
  '  "document_type": "Passport",',
  '  "required_sides": ["front"],',
  '  "next_step": "face_movement"',
  "}",
].join("\n");

function Brand() {
  return (
    <Link className="home-brand" to="/" aria-label="IdentityVerify home">
      <span className="home-brand-mark" aria-hidden="true">
        <ShieldCheck />
      </span>
      <span>IdentityVerify</span>
    </Link>
  );
}

function RuleExplorer() {
  const [country, setCountry] = useState(DEMO_COUNTRIES[0]);
  const allowedTypes = useMemo(
    () => getAllowedDocumentTypes(country.code),
    [country],
  );
  const isMultiDocument = supportsMultipleDocumentTypes(country.code);

  return (
    <aside className="home-rule-explorer" aria-labelledby="demo-title">
      <div className="home-rule-topline">
        <span className="home-live-badge">
          <i />
          Live rule explorer
        </span>
        <span className="home-rule-engine">
          <Workflow />
          Same logic as /v/
        </span>
      </div>

      <div className="home-rule-heading">
        <span className="home-rule-icon">
          <Globe2 />
        </span>
        <div>
          <small>Country-driven routing</small>
          <h2 id="demo-title">What can this applicant use?</h2>
        </div>
      </div>

      <div
        className="home-country-options"
        role="group"
        aria-label="Choose a country"
      >
        {DEMO_COUNTRIES.map((item) => (
          <button
            key={item.code}
            type="button"
            className={country.code === item.code ? "is-active" : ""}
            aria-pressed={country.code === item.code}
            onClick={() => setCountry(item)}
          >
            <span>{item.code}</span>
            {item.label}
          </button>
        ))}
      </div>

      <div className="home-rule-result" aria-live="polite">
        <div className="home-rule-result-head">
          <div>
            <small>{country.label}</small>
            <strong>
              {isMultiDocument ? "3 eligible documents" : "Passport route"}
            </strong>
          </div>
          <span>{isMultiDocument ? "Multi-document" : "Default rule"}</span>
        </div>

        <div className="home-document-options">
          {allowedTypes.map((documentType) => {
            const meta = DOCUMENT_META[documentType];
            const Icon = meta.icon;
            const requiresBack = documentRequiresBack(documentType);

            return (
              <div className="home-document-option" key={documentType}>
                <span>
                  <Icon />
                </span>
                <p>
                  <strong>{meta.label}</strong>
                  <small>{meta.detail}</small>
                </p>
                <em>{requiresBack ? "2 sides" : "1 side"}</em>
              </div>
            );
          })}
        </div>
      </div>

      <p className="home-rule-footnote">
        <CircleCheck />
        The result updates from the production document-requirement module.
      </p>
    </aside>
  );
}

function JourneyPanel() {
  return (
    <div className="home-journey-panel" aria-label="Verification journey preview">
      <div className="home-journey-window">
        <div className="home-journey-window-head">
          <div>
            <span>
              <ShieldCheck />
            </span>
            <p>
              <strong>Verification session</strong>
              <small>Sweden · Passport</small>
            </p>
          </div>
          <em>
            <LockKeyhole />
            In progress
          </em>
        </div>

        <div className="home-journey-body">
          <div className="home-journey-progress">
            <span className="is-complete">
              <Check />
            </span>
            <i className="is-complete" />
            <span className="is-active">2</span>
            <i />
            <span>3</span>
            <i />
            <span>4</span>
          </div>

          <div className="home-journey-step-labels">
            <span>Country</span>
            <span>Document</span>
            <span>Face</span>
            <span>Address</span>
          </div>

          <div className="home-capture-card">
            <div>
              <small>Current step</small>
              <h3>Capture passport</h3>
              <p>Keep the photo page flat and show all four corners.</p>
            </div>

            <div className="home-capture-frame">
              <span className="is-top-left" />
              <span className="is-top-right" />
              <span className="is-bottom-left" />
              <span className="is-bottom-right" />
              <div className="home-passport-shape">
                <ScanFace />
                <div>
                  <i />
                  <i />
                  <i />
                </div>
              </div>
              <b />
            </div>

            <div className="home-capture-status">
              <CircleCheck />
              Four corners visible
              <ChevronRight />
            </div>
          </div>
        </div>
      </div>

      <div className="home-journey-float is-routing">
        <Globe2 />
        <p>
          <small>Rule applied</small>
          <strong>Passport · 1 side</strong>
        </p>
      </div>
      <div className="home-journey-float is-next">
        <ScanFace />
        <p>
          <small>Up next</small>
          <strong>Face movement</strong>
        </p>
      </div>
    </div>
  );
}

function DocumentFeature() {
  return (
    <article className="home-feature-card is-document">
      <div className="home-feature-card-head">
        <span>
          <FileCheck2 />
        </span>
        <em>01</em>
      </div>
      <h3>Country-aware documents</h3>
      <p>
        Present only the identity documents supported for the applicant's
        market.
      </p>
      <div className="home-feature-visual home-country-visual">
        <div className="home-mini-select">
          <small>Applicant country</small>
          <strong>
            <span>SE</span>
            Sweden
            <ChevronRight />
          </strong>
        </div>
        <div className="home-mini-doc-grid">
          <span className="is-active">
            <FileText />
            Passport
          </span>
          <span>
            <IdCard />
            ID card
          </span>
          <span>
            <IdCard />
            Driver's license
          </span>
        </div>
      </div>
    </article>
  );
}

function SidesFeature() {
  return (
    <article className="home-feature-card is-sides">
      <div className="home-feature-card-head">
        <span>
          <Layers3 />
        </span>
        <em>02</em>
      </div>
      <h3>Required-side capture</h3>
      <p>
        Move from one-side passport capture to front-and-back card capture
        without extra branching.
      </p>
      <div className="home-feature-visual home-sides-visual">
        <div className="home-side-card is-front">
          <span>FRONT</span>
          <ScanFace />
          <i />
          <i />
        </div>
        <div className="home-side-card is-back">
          <span>BACK</span>
          <div />
          <i />
          <i />
        </div>
        <small>
          <CircleCheck />
          2 of 2 sides ready
        </small>
      </div>
    </article>
  );
}

function ContinuityFeature() {
  return (
    <article className="home-feature-card is-continuity">
      <div className="home-feature-card-head">
        <span>
          <Zap />
        </span>
        <em>03</em>
      </div>
      <h3>One continuous journey</h3>
      <p>
        Keep document capture, guided face movement, and proof of address in a
        single session.
      </p>
      <div className="home-feature-visual home-continuity-visual">
        <div className="home-mini-timeline">
          <span className="is-complete">
            <Check />
            <small>Document</small>
          </span>
          <i className="is-complete" />
          <span className="is-active">
            <ScanFace />
            <small>Face movement</small>
          </span>
          <i />
          <span>
            <FileText />
            <small>Address</small>
          </span>
        </div>
      </div>
    </article>
  );
}

function CapabilityList({ items }) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item}>
          <CircleCheck />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "IdentityVerify — Country-aware KYC capture";

    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Present eligible identity documents by country, capture the required sides, and guide applicants through one focused KYC journey.",
      );
    }
  }, []);

  return (
    <div className="home-page">
      <header className="home-header">
        <div className="home-shell home-header-inner">
          <Brand />

          <nav className="home-nav" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <a className="home-header-action" href="#demo">
            Try country rules
            <ArrowRight />
          </a>

          <button
            type="button"
            className="home-menu-button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {menuOpen && (
          <nav className="home-mobile-nav" aria-label="Mobile navigation">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
                <ChevronRight />
              </a>
            ))}
          </nav>
        )}
      </header>

      <main>
        <section className="home-hero" id="demo">
          <div className="home-hero-grid" aria-hidden="true" />
          <div className="home-shell home-hero-layout">
            <div className="home-hero-copy">
              <p className="home-kicker">
                <span />
                For digital products onboarding across markets
              </p>
              <h1>
                Ask for the right document.
                <span>Capture every required side.</span>
              </h1>
              <p className="home-hero-description">
                IdentityVerify turns country and document rules into a focused
                KYC journey—then guides applicants through document capture,
                face movement, and proof of address.
              </p>

              <div className="home-hero-actions">
                <a className="home-button home-button-primary" href="#demo">
                  Try the rule explorer
                  <ArrowRight />
                </a>
                <a className="home-button home-button-secondary" href="#flow">
                  See the full journey
                </a>
              </div>

              <div className="home-hero-notes" aria-label="Platform qualities">
                <span>
                  <Check />
                  Country-aware
                </span>
                <span>
                  <Check />
                  Mobile-first
                </span>
                <span>
                  <Check />
                  Provider-ready
                </span>
              </div>
            </div>

            <RuleExplorer />
          </div>
        </section>

        <section className="home-facts" aria-label="Document rule coverage">
          <div className="home-shell home-fact-grid">
            <div>
              <strong>{MULTI_DOCUMENT_COUNTRY_COUNT}</strong>
              <p>
                <b>listed markets</b>
                <span>Passport, ID card, or driver's license</span>
              </p>
            </div>
            <div>
              <strong>All</strong>
              <p>
                <b>other markets</b>
                <span>Passport-only route</span>
              </p>
            </div>
            <div>
              <strong>1–2</strong>
              <p>
                <b>required sides</b>
                <span>Decided by document type</span>
              </p>
            </div>
          </div>
        </section>

        <section className="home-platform" id="platform">
          <div className="home-shell">
            <div className="home-section-heading">
              <p>Product, not a pile of screens</p>
              <h2>Less guessing for applicants. Less branching for your team.</h2>
              <span>
                The journey applies the rules before asking the applicant to
                act, so every screen has one clear job.
              </span>
            </div>

            <div className="home-feature-grid">
              <DocumentFeature />
              <SidesFeature />
              <ContinuityFeature />
            </div>
          </div>
        </section>

        <section className="home-flow" id="flow">
          <div className="home-shell home-flow-layout">
            <div className="home-flow-copy">
              <p>One connected journey</p>
              <h2>From session link to the next required check.</h2>
              <span>
                Applicants move through relevant steps in order. Your capture
                experience stays coherent even as verification providers change.
              </span>

              <ol className="home-flow-steps">
                {JOURNEY_STEPS.map((step) => (
                  <li key={step.number}>
                    <span>{step.number}</span>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <JourneyPanel />
          </div>
        </section>

        <section className="home-status" id="extensibility">
          <div className="home-shell">
            <div className="home-section-heading home-status-heading">
              <p>Honest by design</p>
              <h2>Clear about what runs today—and what plugs in next.</h2>
              <span>
                Capture and provider verification are separated, so deeper
                checks can be added without changing the applicant journey.
              </span>
            </div>

            <div className="home-status-grid">
              <article className="home-status-card is-live">
                <div className="home-status-card-head">
                  <span>
                    <i />
                    Available now
                  </span>
                  <ShieldCheck />
                </div>
                <h3>Guided capture and routing</h3>
                <p>
                  The current flow handles the decisions and capture guidance
                  users need to complete a session.
                </p>
                <CapabilityList
                  items={[
                    "Country-aware document choices",
                    "Passport or front-and-back capture",
                    "Four-corner document guidance",
                    "Guided face-angle movement",
                    "Proof-of-address upload",
                  ]}
                />
              </article>

              <article className="home-status-card is-ready">
                <div className="home-status-card-head">
                  <span>
                    <i />
                    Provider-ready
                  </span>
                  <Braces />
                </div>
                <h3>Advanced verification APIs</h3>
                <p>
                  Extension points are reserved for deeper checks. They remain
                  non-blocking until a provider is configured.
                </p>
                <CapabilityList
                  items={[
                    "Document authenticity and validity",
                    "Biometric liveness and spoof detection",
                    "Image and replay-attack screening",
                    "Server-side decision policy",
                  ]}
                />
                <small className="home-provider-note">
                  Not presented as active verification today.
                </small>
              </article>
            </div>
          </div>
        </section>

        <section className="home-developers" id="developers">
          <div className="home-shell home-developer-layout">
            <div className="home-developer-copy">
              <p>Built to extend</p>
              <h2>Stable capture outside. Replaceable providers inside.</h2>
              <span>
                Keep the applicant journey independent from document and
                biometric vendors. The gateway can accept provider responses
                later without rewriting existing screens.
              </span>
              <div className="home-developer-points">
                <p>
                  <Braces />
                  Structured session state
                </p>
                <p>
                  <Fingerprint />
                  Vendor-neutral verification gateway
                </p>
                <p>
                  <ShieldCheck />
                  Non-blocking when unconfigured
                </p>
              </div>
            </div>

            <div className="home-code-wrap">
              <div className="home-code-card">
                <div className="home-code-header">
                  <span>
                    <i />
                    Session state
                  </span>
                  <em>JSON</em>
                </div>
                <pre>
                  <code>{CODE_SAMPLE}</code>
                </pre>
                <div className="home-code-footer">
                  <CircleCheck />
                  Routing rule applied
                </div>
              </div>
              <div className="home-code-tag">
                <Workflow />
                <span>
                  <small>Integration boundary</small>
                  AdvancedVerificationGateway
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="home-final">
          <div className="home-shell home-final-inner">
            <span className="home-final-icon">
              <Globe2 />
            </span>
            <p>Start with the product rule that matters</p>
            <h2>Choose a country. See the exact document path.</h2>
            <a className="home-button home-button-light" href="#demo">
              Try the live rule explorer
              <ArrowRight />
            </a>
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="home-shell home-footer-inner">
          <Brand />
          <p>Document · Face movement · Address</p>
          <span>© {new Date().getFullYear()} IdentityVerify</span>
        </div>
      </footer>
    </div>
  );
}
